import { useEffect, useRef, useState } from "react";

import { TYPE_WELLS } from "constants/settings.constants";
import { useChartsContext } from "contexts";

import { useChartDependencies, useChartEntities } from "hooks/charts";

import { LegendItemModel } from "models";

import { getTitle } from "components/arps/utils/getTitle";
import useTypeWellSeries from "components/charts/hooks/useTypeWellSeries";
import { createLegendItem } from "components/charts/utils";

const useTypeWellLegendItems = (): LegendItemModel[] => {
  // context
  const { id: chartId, entityKind } = useChartsContext();
  const { normalizeBy, typewells } = useChartEntities(chartId);
  const typeWellsActive = typewells?.active;

  // state
  const { checkedGlobalTypeWells, globalNormalizeBy, normalizeTypeWell } =
    useChartDependencies(entityKind);
  const { typeWellSeries } = useTypeWellSeries(
    checkedGlobalTypeWells,
    normalizeBy?.active && normalizeBy?.properties
      ? normalizeBy.properties
      : globalNormalizeBy,
    normalizeBy?.active,
    normalizeTypeWell,
    typeWellsActive
  );

  const [items, setItems] = useState<LegendItemModel[]>([]);

  // derived state
  const isTypeWellsEnabled = typewells?.active;

  const prevTypeWellSeriesRef = useRef(null);

  // effects
  useEffect(() => {
    // useTypeWellSeries will continously return an empty array if typewells is not enabled
    // need a ref to not setItems continously which causes an infinite loop
    if (
      JSON.stringify(prevTypeWellSeriesRef.current) === JSON.stringify(typeWellSeries)
    ) {
      return;
    }
    prevTypeWellSeriesRef.current = typeWellSeries;

    if (!isTypeWellsEnabled || !checkedGlobalTypeWells?.length) {
      setItems([]);
      return;
    }
    const nextList = checkedGlobalTypeWells
      .filter((item) => {
        // only include type wells in legend that are shown on the chart
        // Compare using enhanced name (with rescat) to match typeWellSeries
        const enhancedName = getTitle(item, TYPE_WELLS);
        return (
          typeWellSeries.some((tw) => tw.name === enhancedName) || item.type === "folder"
        );
      })
      .map((item) => {
        return item.type === "folder"
          ? item.children
              .filter((item) => {
                const enhancedChildName = getTitle(item, TYPE_WELLS);
                return typeWellSeries.some((tw) => tw.name === enhancedChildName);
              })
              .map((child) => {
                const enhancedChildName = getTitle(child, TYPE_WELLS);
                return createLegendItem(
                  enhancedChildName,
                  true,
                  child.color,
                  "#555555",
                  child.id
                );
              })
          : (() => {
              const enhancedName = getTitle(item, TYPE_WELLS);
              return createLegendItem(enhancedName, true, item.color, "#555555", item.id);
            })();
      })
      .flatMap((x) => x);

    setItems(nextList);
  }, [isTypeWellsEnabled, checkedGlobalTypeWells, typeWellSeries]);

  return items;
};

export default useTypeWellLegendItems;
